/**
 * AWS Shadow AI Discovery — Connect Dialog
 *
 * Two-phase connect wizard: show the read-only CloudFormation setup
 * instructions, then collect the role ARN and the regions to scan. The
 * AssumeRole handshake runs on the backend; its error message is surfaced
 * verbatim so the user can act on the real AWS reason (e.g. trust policy
 * mismatch, ExternalId mismatch).
 */

import { ReactNode, useState } from "react";
import {
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AwsDiscoverySetupInstructions } from "./AwsDiscoverySetupInstructions";
import {
  useAwsDiscoverySetupInstructions,
  useCreateAwsDiscoveryConnection,
} from "@/hooks/useAwsDiscovery";
import { AWS_BEDROCK_REGIONS } from "@/constants/aws";

interface AwsDiscoveryConfigDialogProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Called after a connection is successfully created (e.g. to navigate). */
  onConnected?: () => void;
}

const DEFAULT_REGIONS = ["us-east-1", "us-west-2"];

export function AwsDiscoveryConfigDialog({
  children,
  open,
  onOpenChange,
  onConnected,
}: AwsDiscoveryConfigDialogProps) {
  const [showInstructions, setShowInstructions] = useState(true);
  const [connectionName, setConnectionName] = useState("");
  const [roleArn, setRoleArn] = useState("");
  const [regions, setRegions] = useState<string[]>(DEFAULT_REGIONS);

  const { data: setupInstructions, isLoading: loadingInstructions } =
    useAwsDiscoverySetupInstructions(open ?? false);

  const createMutation = useCreateAwsDiscoveryConnection();

  const handleReset = () => {
    setConnectionName("");
    setRoleArn("");
    setRegions(DEFAULT_REGIONS);
    setShowInstructions(true);
    createMutation.reset();
  };

  const toggleRegion = (region: string) => {
    setRegions((prev) =>
      prev.includes(region) ? prev.filter((r) => r !== region) : [...prev, region]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setupInstructions) return;

    try {
      await createMutation.mutateAsync({
        name: connectionName.trim(),
        role_arn: roleArn.trim(),
        external_id: setupInstructions.external_id,
        regions,
      });
      onConnected?.();
      setTimeout(() => handleReset(), 1500);
    } catch {
      // Error surfaced via createMutation.error below.
    }
  };

  const isFormValid =
    connectionName.trim().length > 0 &&
    roleArn.trim().startsWith("arn:aws:iam::") &&
    regions.length > 0 &&
    !!setupInstructions;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleReset();
        onOpenChange?.(nextOpen);
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Connect an AWS account</DialogTitle>
          <DialogDescription>
            Deploy one read-only role and Vortal will discover the AI infrastructure
            running in your account — Bedrock, SageMaker, and AI-key signals.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Step 1 — Setup instructions (collapsible) */}
          <div className="rounded-lg border bg-muted/30">
            <button
              type="button"
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium"
              onClick={() => setShowInstructions((v) => !v)}
            >
              <span>Step 1 — Deploy the read-only CloudFormation stack</span>
              {showInstructions ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>

            {showInstructions && (
              <div className="px-4 pb-4 border-t">
                <AwsDiscoverySetupInstructions
                  setupInstructions={setupInstructions}
                  isLoading={loadingInstructions}
                />
              </div>
            )}
          </div>

          {/* Step 2 — Connection details */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground">
              Step 2 — Enter connection details
            </h4>

            {createMutation.isSuccess && (
              <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Connected. Handshake succeeded.</p>
                  <p className="text-xs mt-1">
                    Vortal can assume your role. Run a discovery scan to populate your
                    AI Bill of Materials.
                  </p>
                </div>
              </div>
            )}

            {createMutation.isError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Couldn't connect</p>
                  {/* Backend returns the mapped AWS message; show it verbatim. */}
                  <p className="text-xs mt-1">
                    {createMutation.error?.message ||
                      "Unable to assume the role. Check the role ARN, External ID, and trust policy, then try again."}
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="aws-discovery-name">
                Connection name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="aws-discovery-name"
                placeholder="prod-account"
                value={connectionName}
                onChange={(e) => setConnectionName(e.target.value)}
                autoComplete="off"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="aws-discovery-role-arn">
                Role ARN <span className="text-destructive">*</span>
              </Label>
              <Input
                id="aws-discovery-role-arn"
                placeholder="arn:aws:iam::123456789012:role/VortalShadowAiDiscovery"
                value={roleArn}
                onChange={(e) => setRoleArn(e.target.value)}
                autoComplete="off"
                required
              />
              <p className="text-xs text-muted-foreground">
                From the CloudFormation stack's Outputs tab (RoleArn).
              </p>
            </div>

            <div className="space-y-2">
              <Label>
                Regions to scan <span className="text-destructive">*</span>
              </Label>
              <p className="text-xs text-muted-foreground">
                Pick the regions you run AI workloads in. Fewer regions means faster,
                cheaper scans.
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 max-h-48 overflow-y-auto rounded-md border p-3">
                {AWS_BEDROCK_REGIONS.map((region) => (
                  <label
                    key={region}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={regions.includes(region)}
                      onCheckedChange={() => toggleRegion(region)}
                      aria-label={region}
                    />
                    <span className="font-mono text-xs">{region}</span>
                  </label>
                ))}
              </div>
              {regions.length === 0 && (
                <p className="text-xs text-destructive">Select at least one region.</p>
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange?.(false)}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!isFormValid || createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verifying handshake…
                  </>
                ) : (
                  "Connect account"
                )}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
